import { runTimeFormatter } from './runTimingFormat';
import { T, useGT, useLocale } from 'gt-react';
import { RobotIcon } from '@phosphor-icons/react';
import { formatDatetimeWithSeconds } from 'src/utils/dateUtils';
import { TaskRunEvidenceButton } from './TaskRunEvidenceButton';
import type { OperatorEvent } from './groupTimelineEvents';
import { recordedTimelineAgentName } from './taskRunPresentation';

export function TaskOperatorEventGroup({
  entries,
  agentNameById,
  rootTaskId,
  canInspectRuns,
  isLast,
}: {
  entries: OperatorEvent[];
  agentNameById: Map<string, string>;
  rootTaskId: string;
  canInspectRuns: boolean;
  isLast: boolean;
}) {
  const gt = useGT();
  const first = entries[0];
  if (!first) return null;
  const name =
    recordedTimelineAgentName(first) ??
    agentNameById.get(
      first.actor.type === 'agent' ? (first.actor.agentDefinitionId ?? '') : '',
    ) ??
    gt('Agent', { context: 'timeline actor fallback label' });
  return (
    <li className="flex gap-2.5">
      <div className="flex w-6 shrink-0 flex-col items-center">
        <span className="mt-0.5 flex size-6 items-center justify-center rounded-full border bg-muted text-muted-foreground">
          <RobotIcon className="size-3.5" />
        </span>
        {!isLast && <span className="w-px flex-1 bg-border" aria-hidden />}
      </div>
      <div className="min-w-0 flex-1 pb-3">
        <div className="flex items-center gap-2 py-1 text-xs font-medium">
          {name}
          {first.taskId !== rootTaskId && (
            <span className="text-muted-foreground">
              <T context="badge marking a timeline entry from a subtask">
                subtask
              </T>
            </span>
          )}
        </div>
        {!canInspectRuns && (
          <p className="text-xs text-muted-foreground">
            <T context="task run inspection permission">
              Agent access is required to inspect run details.
            </T>
          </p>
        )}
        <ol>
          {entries.map((entry) => (
            <li key={entry.id} data-kind="operator_step">
              <OperatorEventRow
                entry={entry}
                agentName={name}
                allowed={canInspectRuns}
              />
            </li>
          ))}
        </ol>
      </div>
    </li>
  );
}

function OperatorEventRow({
  entry,
  agentName,
  allowed,
}: {
  entry: OperatorEvent;
  agentName: string;
  allowed: boolean;
}) {
  const content = <OperatorEventContent entry={entry} />;
  const rowClass =
    'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs';
  if (!allowed) return <div className={rowClass}>{content}</div>;
  return (
    <TaskRunEvidenceButton
      agentRunId={entry.agentRunId}
      agentName={agentName}
      operatorStepId={entry.operatorStepId}
      eventDetails={entry}
      allowed
      trigger={
        <button
          type="button"
          className={`${rowClass} cursor-pointer hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
        >
          {content}
        </button>
      }
    />
  );
}

function OperatorEventContent({ entry }: { entry: OperatorEvent }) {
  const locale = useLocale();
  const gt = useGT();
  return (
    <>
      <span className="w-7 shrink-0 tabular-nums text-muted-foreground">
        #{entry.stepIndex}
      </span>
      <span className="min-w-0 flex-1 break-words">
        {entry.actionTools
          ?.map((tool) => tool.replaceAll('_', ' '))
          .join(', ') ||
          gt('Operator step', {
            context: 'task timeline operator event fallback action',
          })}
      </span>
      <span className="rounded bg-muted px-1 text-muted-foreground">
        {entry.status}
      </span>
      <time
        className="shrink-0 tabular-nums text-muted-foreground"
        dateTime={entry.timestamp.toISOString()}
        title={formatDatetimeWithSeconds(entry.timestamp)}
        aria-label={formatDatetimeWithSeconds(entry.timestamp)}
      >
        {runTimeFormatter(locale, 'seconds').format(entry.timestamp)}
      </time>
    </>
  );
}
